import React, { useCallback, useEffect, useRef } from 'react';
import { useWindowWidth } from '@react-hook/window-size/throttled';

import { getGridPosition } from '~/views/CollectionView/CollectionCardGrid/utils';

import constants from '~/sass/consts.module.scss';

import styles from './SpecialGroupCanvas.module.scss';

interface ISpecialGroupsCanvasProps {
  height: number;
  width: number;
  style: React.CSSProperties;
  imgElement: HTMLImageElement | null;
  slotPlacement: Array<boolean>;
  numberOfColumns: number;
}

const SpecialGroupsCanvas: React.FC<ISpecialGroupsCanvasProps> = ({
  style,
  height,
  width,
  imgElement,
  slotPlacement,
  numberOfColumns,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= Number(constants.mediumMobileView);

  const paintImageOnCanvas = useCallback(() => {
    if (!canvasRef.current || !imgElement?.complete) {
      return;
    }
    const canvasElement = canvasRef.current;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ctx = canvasElement.getContext('2d')!;
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#000';
    ctx.globalCompositeOperation = 'source-over';

    const numberOfRows = slotPlacement.length / numberOfColumns;

    slotPlacement.forEach((isCardPlaced, i) => {
      if (!isCardPlaced) return;
      const slotColumn = i % numberOfColumns;
      const slotRow = Math.floor(i / numberOfColumns);

      const startX =
        slotColumn === 0
          ? 0
          : getGridPosition({
              index: slotColumn - 1,
              numberOfSegments: numberOfColumns,
              parentSize: width,
              isMobile,
            });

      const startY =
        slotRow === 0
          ? 0
          : getGridPosition({
              index: slotRow - 1,
              numberOfSegments: numberOfRows,
              parentSize: height,
              isMobile,
            });

      const endX =
        slotColumn === numberOfColumns - 1
          ? width
          : getGridPosition({
              index: slotColumn,
              numberOfSegments: numberOfColumns,
              parentSize: width,
              isMobile,
            });

      const endY =
        slotRow === numberOfRows - 1
          ? height
          : getGridPosition({
              index: slotRow,
              numberOfSegments: numberOfRows,
              parentSize: height,
              isMobile,
            });

      ctx.fillRect(startX, startY, endX - startX, endY - startY);
    });

    ctx.globalCompositeOperation = 'source-in';
    ctx.drawImage(imgElement, 0, 0, width, height);
  }, [imgElement, height, width, slotPlacement, numberOfColumns, isMobile]);

  useEffect(() => {
    if (!imgElement) {
      return undefined;
    }
    imgElement.addEventListener('load', paintImageOnCanvas);
    return () => {
      imgElement.removeEventListener('load', paintImageOnCanvas);
    };
  }, [imgElement, paintImageOnCanvas]);

  useEffect(() => {
    paintImageOnCanvas();
  }, [paintImageOnCanvas]);

  return (
    <canvas
      className={styles.canvas}
      ref={canvasRef}
      style={style}
      height={height}
      width={width}
    />
  );
};

function shouldSkipRender(
  prevProps: ISpecialGroupsCanvasProps,
  newProps: ISpecialGroupsCanvasProps,
): boolean {
  const isPlacementEqual = prevProps.slotPlacement.every(
    (placement, i) => placement === newProps.slotPlacement[i],
  );

  return (
    isPlacementEqual &&
    prevProps.height === newProps.height &&
    prevProps.width === newProps.width &&
    prevProps.style === newProps.style &&
    prevProps.imgElement === newProps.imgElement &&
    prevProps.numberOfColumns === newProps.numberOfColumns
  );
}

export default React.memo(SpecialGroupsCanvas, shouldSkipRender);
